import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { MessageSquare, Wallet, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SMS Bundle top-up panel on the shop /billing page.
 *
 * Fetches the current bundle credit + configured prices, renders three
 * buy buttons (200 / 400 / 1000 SMS). Each button opens a Stripe
 * Checkout in the current tab; fulfilment happens webhook-side. When
 * the user comes back to /billing after checkout, the balance and
 * recent-purchases list will reflect the new credit.
 *
 * Available to BOTH Standard and Premium — bundles top up whichever
 * plan you're on.
 */
export const SmsBundlesPanel = ({ shopId }) => {
  const [config, setConfig] = useState(null);
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState('');
  const [busyQty, setBusyQty] = useState(null);

  const authHeaders = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return { Authorization: `Bearer ${session?.access_token}` };
  };

  const load = async () => {
    try {
      const h = await authHeaders();
      const [cfgRes, balRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/sms-bundle/config`, { headers: h }),
        fetch(`${BACKEND_URL}/api/billing/sms-bundle/balance/${shopId}`, { headers: h }),
      ]);
      if (!cfgRes.ok) throw new Error(`config HTTP ${cfgRes.status}`);
      if (!balRes.ok) throw new Error(`balance HTTP ${balRes.status}`);
      setConfig(await cfgRes.json());
      setBalance(await balRes.json());
      setError('');
    } catch (e) { setError(e.message); }
  };
  useEffect(() => { if (shopId) load(); /* eslint-disable-next-line */ }, [shopId]);

  const purchase = async (quantity) => {
    setBusyQty(quantity);
    try {
      const h = await authHeaders();
      const returnTo = `${window.location.origin}/billing`;
      const res = await fetch(`${BACKEND_URL}/api/billing/sms-bundle/checkout`, {
        method: 'POST',
        headers: { ...h, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_id: shopId,
          quantity,
          success_url: `${returnTo}?bundle=success`,
          cancel_url:  `${returnTo}?bundle=cancelled`,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      window.location.href = j.url;
    } catch (e) {
      setError(e.message);
      setBusyQty(null);
    }
  };

  return (
    <div className="panel" data-testid="sms-bundles-panel">
      <div className="ph">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MessageSquare size={14} /> SMS bundles
        </h3>
        {balance && (
          <span
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            data-testid="sms-bundle-balance"
          >
            <Wallet size={13} />
            <b>{balance.sms_bundle_credit}</b> credits
          </span>
        )}
      </div>

      <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--mute)' }}>
        Top up your monthly SMS allowance. Credits are consumed after your monthly
        plan cap and carry forward — they never expire.
      </div>

      {error && (
        <div style={{ padding: '0 16px 12px', color: 'var(--red)', fontSize: 12 }} data-testid="sms-bundle-error">
          {error}
        </div>
      )}

      {config && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 10,
            padding: '0 16px 16px',
          }}
        >
          {config.bundles.map((b) => (
            <button
              key={b.quantity}
              type="button"
              className="btn"
              onClick={() => purchase(b.quantity)}
              disabled={busyQty !== null}
              data-testid={`sms-bundle-buy-${b.quantity}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
                padding: 12,
                border: '1px solid var(--line, #e5e7eb)',
                borderRadius: 10,
                background: 'var(--bg2, #fff)',
                cursor: busyQty !== null ? 'wait' : 'pointer',
                opacity: busyQty !== null && busyQty !== b.quantity ? 0.55 : 1,
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>{b.quantity} SMS</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--acc, #6366f1)', marginTop: 4 }}>
                £{b.price_gbp.toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--mute)', marginTop: 4 }}>
                £{(b.price_gbp / b.quantity).toFixed(3)} per SMS
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  color: busyQty === b.quantity ? 'var(--mute)' : 'var(--acc, #6366f1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                {busyQty === b.quantity ? 'Redirecting…' : <>Buy now <ExternalLink size={11} /></>}
              </div>
            </button>
          ))}
        </div>
      )}

      {balance && balance.recent_purchases && balance.recent_purchases.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line, #e5e7eb)', padding: '10px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--mute)', marginBottom: 6 }}>
            Recent purchases
          </div>
          {balance.recent_purchases.map((p, i) => (
            <div
              key={i}
              style={{ display: 'flex', gap: 8, fontSize: 12, padding: '3px 0', color: 'var(--mute)' }}
            >
              <span>{new Date(p.created_at).toLocaleDateString('en-GB')}</span>
              <span><b style={{ color: 'var(--ink)' }}>{p.quantity} SMS</b></span>
              <span>£{(p.price_paid_pence / 100).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
